import { isActiveChangeOrder } from '../util/changeOrder'
import { urlToId } from '../util/misc'

const showAll = false

const systemsVisible = (systemsAll, soldSystemId) => {
  let systems = systemsAll
  if (showAll) {
    return systems
  } else {
    systems = systems.filter((s) => s.show_customer === true)
  }

  if (soldSystemId) {
    systems = systems.filter((s) => s.id === soldSystemId)
  }
  return systems
}

const systemsVisibleAndSortedByOrderAscending = (systemsAll, soldSystemId) => {
  return systemsVisible(systemsAll, soldSystemId)
    .slice() // why we need slice the array?
    .sort((a, b) => {
      return a.order > b.order
    })
}

//  We want to handle all data preparation server-side and remove most field calculation from populate.js
//
// Temporary workaround. Previously we used system.data but this does not include any custom placeholders such as componentWarranties etc.
// Now we will keep using system.data but we will inject all the missing placeholders.
// In future, we should merge these into a single object to avoid unnecessary data duplication and confusion.
const systemDataIncudingPlaceholders = (system) => {
  var keysToInject = [
    'systemKwStc',
    'inverterTotalRatingKw',
    'hasMultipleModuleTypes',
    'moduleTypesSummary',
    'moduleTypeCount',
    'moduleCodes',
    'moduleManufacturers',
    'module_codes_with_costs',
    'module_codes_with_quantities',
    'moduleQuantity',
    'moduleEfficiency',
    'moduleCode',
    'moduleManufacturer',
    'moduleWattStc',
    'moduleProductWarranty',
    'modulePerformanceWarranty',
    'moduleQuantitiesWithCodes',
    'moduleManufacturersWithQuantities',
    'moduleManufacturersWithQuantitiesAndCodes',
    'moduleDimensionsFormatted',
    'moduleSpecsFormatted',
    'moduleTableRows',
    'moduleListSimple',
    'moduleListDetailed',
    'systemOutputAnnualkWh',
    'inverterManufacturer',
    'inverterQuantity',
    'inverterProductWarranty',
    'inverterAdditionalPartsWarranty',
    'inverterCodes',
    'batteryManufacturer',
    'batteryCodes',
    'batteryTotalKwh',
    'otherComponentCodes',
    'batteryProductWarranty',
    'systemBatteryKwhOptimal',
    'systemBatteryUsableKwh',
    'batteryThroughput',
    'batteryCycleLife',
    'inverterNominalAcPower',
    'panelOrientations',
    'systemTotalLosses',
    'inverterLosses',
    'optimizerLosses',
    'shadingLossAnnual',
    'outputCalculator',
    'performanceAdjustment',
    'systemNetPresentValue',
    'systemPaybackYear',
    'systemDiscountedPaybackYear',
    'systemReturnOnInvestment',
    'systemIrr',
    'systemLcoe',
    'systemFinancialDiscountRate',
    'systemUtilityInflationAnnual',
    'systemFeedInTariffInflationAnnual',
    'proposedUsageAdjustment',
    'outputCalculationNotes',
    'componentWarranties',

    'disclaimer_system_hardware_components',
    'disclaimer_system_performance',
    'disclaimer_system_performance_new',
    'disclaimer_daily_energy_flows',
    'disclaimer_environmental_benefits',
    'content_block_1',
    'content_block_2',
    'content_block_3',

    'tile_online_proposals',
    'tile_pdf_proposals',
    'module_codes_with_details',
    'inverter_codes_with_details',
    'other_codes_with_details',
    'battery_codes_with_details',
    'catch_power',
  ]

  var systemWithPlaceholders = system.data

  // Notice: This will modify the raw system data with extra keys/data but we assume this is ok.
  keysToInject.forEach((key) => {
    systemWithPlaceholders[key] = system[key]
  })

  return systemWithPlaceholders
}

export function findActiveChangeOrderInList(changeOrders) {
  return (changeOrders || []).find((co) => isActiveChangeOrder(co)) || null
}

function preferNonEmptySelectedSystems(primary, fallback) {
  if (Array.isArray(primary) && primary.length > 0) return primary
  if (Array.isArray(fallback) && fallback.length > 0) return fallback
  return primary ?? fallback
}

/** Merge BE-authored proposal payload with the matching `change_orders[]` list entry. */
function mergeChangeOrderSources(activeFromList, fromProposal) {
  if (!fromProposal) {
    return activeFromList || null
  }
  if (!activeFromList || activeFromList.id !== fromProposal.id) {
    return fromProposal
  }
  return {
    ...fromProposal,
    ...activeFromList,
    selected_systems: preferNonEmptySelectedSystems(activeFromList.selected_systems, fromProposal.selected_systems),
    system_change_orders: fromProposal.system_change_orders ?? activeFromList.system_change_orders,
    change_order_template_content:
      fromProposal.change_order_template_content ?? activeFromList.change_order_template_content,
  }
}

/** V3 change orders use `selected_systems` instead of `allow_review` + unsold-only flow. */
function getActiveInitiatedChangeOrder(selectedProject) {
  const changeOrders = selectedProject.change_orders || []
  const fromProposal = selectedProject.proposal_data?.initiated_change_order

  if (fromProposal) {
    const listEntry = changeOrders.find((co) => co.id === fromProposal.id) || null
    return mergeChangeOrderSources(listEntry, fromProposal)
  }

  return findActiveChangeOrderInList(changeOrders)
}

function getV3SelectedSystemIds(initiatedChangeOrder) {
  const raw = initiatedChangeOrder?.selected_systems
  if (raw?.length) {
    return raw.map((id) => Number(id))
  }
  const systemChangeOrders = initiatedChangeOrder?.system_change_orders
  if (systemChangeOrders && typeof systemChangeOrders === 'object') {
    return Object.keys(systemChangeOrders).map((id) => Number(id))
  }
  return []
}

function isV3InitiatedChangeOrder(initiatedChangeOrder) {
  return getV3SelectedSystemIds(initiatedChangeOrder).length > 0
}

function getAvailableSystems(selectedProject) {
  const proposalSystemsRaw = selectedProject.proposal_data?.systems ?? []
  const changeOrders = selectedProject.change_orders ?? []

  const soldSystemId = selectedProject.system_sold ? urlToId(selectedProject.system_sold) : null
  const originalSystemSoldId = selectedProject.original_system_sold
    ? urlToId(selectedProject.original_system_sold)
    : null
  const changeOrderSystems = changeOrders.filter((c) => c.is_complete).flatMap((c) => c.system_sold_id)
  const unsoldSystems = proposalSystemsRaw
    .filter((s) => {
      return s.data.id !== soldSystemId && s.data.id !== originalSystemSoldId && !changeOrderSystems.includes(s.data.id)
    })
    .map((s) => systemDataIncudingPlaceholders(s))
  const proposalSystems = proposalSystemsRaw.map((s) => systemDataIncudingPlaceholders(s))
  const initiatedChangeOrder = getActiveInitiatedChangeOrder(selectedProject)
  if (initiatedChangeOrder) {
    if (isV3InitiatedChangeOrder(initiatedChangeOrder)) {
      const v3SystemIds = getV3SelectedSystemIds(initiatedChangeOrder)
      const v3Systems = proposalSystems.filter((s) => v3SystemIds.includes(s.id))
      return systemsVisibleAndSortedByOrderAscending(v3Systems)
    }
    if (initiatedChangeOrder.allow_review) {
      return systemsVisibleAndSortedByOrderAscending(unsoldSystems)
    }
    return systemsVisibleAndSortedByOrderAscending(proposalSystems, soldSystemId)
  } else if (changeOrders.find((c) => c.is_complete)) {
    const changeOrder = changeOrders.findLast((c) => c.is_complete && c.allow_review)
    if (changeOrder && changeOrder.allow_review) {
      const systems = systemsVisibleAndSortedByOrderAscending(proposalSystems, changeOrder.system_sold)
      return systems
    } else {
      return systemsVisibleAndSortedByOrderAscending(proposalSystems)
    }
  } else {
    const systems = systemsVisibleAndSortedByOrderAscending(proposalSystems)
    return systems
  }
}

function getLatestCompletedChangeOrderPaymentOptionId(selectedProject) {
  const completed = (selectedProject.change_orders || []).filter((c) => c.is_complete)
  if (!completed.length) return null
  const latest = completed[completed.length - 1]
  if (latest.payment_option_sold) return urlToId(latest.payment_option_sold)
  return latest.payment_option_sold_id ?? null
}

export default function parseDataToState(data) {
  //Filter only customer viewable
  const org = data[0]
  const selectedProject = org.projects[0]
  const systems = getAvailableSystems(selectedProject)
  const selectedSystem = systems[0]
  const otherDesigns = org?.projects.filter((m) => m.id !== selectedProject.id)
  const otherAccounts = data.filter((a) => a.id !== org.id)

  // @TODO: This is bogus: data.selectedPaymentOptionId because data is a list of projects!
  let soldPaymentOptionId = selectedProject.payment_option_sold ? urlToId(selectedProject.payment_option_sold) : null
  if (soldPaymentOptionId && selectedProject.system_sold) {
    let soldSystemId = urlToId(selectedProject.system_sold)
    if (selectedProject.proposal_data?.systems?.length > 0) {
      let soldPmtIsPhoenix = false
      selectedProject.proposal_data?.systems?.forEach((sys) => {
        if (sys?.data?.id === soldSystemId && sys.attached_phoenix_payment_option) soldPmtIsPhoenix = true
      })

      if (soldPmtIsPhoenix) soldPaymentOptionId = null
    }
  }
  // try {
  //   selectedPaymentOptionId === data.selectedPaymentOptionId
  //     ? data.selectedPaymentOptionId
  //     : selectedSystem.payment_options[0].id
  // } catch (err) {
  //   console.warn(err)
  //   selectedPaymentOptionId = null
  // }
  const initiatedChangeOrder = getActiveInitiatedChangeOrder(selectedProject)
  const v3ChangeOrderActive = isV3InitiatedChangeOrder(initiatedChangeOrder)
  const changeOrderActive = !!initiatedChangeOrder
  const completedChangeOrderPaymentOptionId = getLatestCompletedChangeOrderPaymentOptionId(selectedProject)

  let selectedPaymentOptionId = null
  if (changeOrderActive && selectedSystem?.payment_options?.length) {
    selectedPaymentOptionId = selectedSystem.payment_options[0].id
  } else if (completedChangeOrderPaymentOptionId) {
    selectedPaymentOptionId = completedChangeOrderPaymentOptionId
  }

  const effectiveSoldPaymentOptionId = completedChangeOrderPaymentOptionId ?? soldPaymentOptionId

  if (effectiveSoldPaymentOptionId && !v3ChangeOrderActive && !changeOrderActive) {
    systems.forEach(
      (system) =>
        (system.payment_options = system.payment_options.filter((po) => po.id === effectiveSoldPaymentOptionId))
    )
  }

  let design
  try {
    design = selectedProject.proposal_data.design
  } catch (err) {
    design = null
  }

  let viewUuids = []
  let selectedViewUuid = null

  // `design.object` may exist without `userData.views` (e.g. empty / non-Studio proposal_data.design).
  const customerViews = design?.object?.userData?.views
  if (customerViews != null && window.ViewHelper?.viewUuidsForCustomer) {
    var viewDetails = window.ViewHelper.viewUuidsForCustomer(customerViews)
    viewUuids = viewDetails.viewUuids
    selectedViewUuid = viewDetails.selectedViewUuid
  }

  //#@TODO: What happens if more than one org? which org should be fetch user from???
  const user = org.user

  return {
    loading: false, //moved to loadingInitialData
    org: org,
    testimonials: selectedProject['testimonials'],
    user: user,
    selectedProject: selectedProject,
    systems: systems,
    selectedSystem: selectedSystem,
    otherDesigns: otherDesigns,
    otherAccounts: otherAccounts,
    viewUuids: viewUuids,
    selectedViewUuid: selectedViewUuid,
    selectedPaymentOptionId: selectedPaymentOptionId,
    proposalTemplateSettings: selectedProject.proposal_data?.proposal_template_settings,
    fullCalcReady: !selectedProject.simulate_first_year_only,
    changeOrders: selectedProject.change_orders ?? [],
    changeOrderProposal: getActiveInitiatedChangeOrder(selectedProject),
  }
}
